import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Network, Wrench, Building2, Calculator, FileText, Briefcase, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEMBER_TYPES = {
  artisan: { label: 'Artisan / Société', icon: Wrench, color: 'text-emerald-500' },
  affilie: { label: 'Affilié', icon: Briefcase, color: 'text-blue-500' },
  chiffreur: { label: 'Chiffreur', icon: Calculator, color: 'text-amber-500' },
  analyste: { label: 'Analyste', icon: FileText, color: 'text-cyan-500' },
  partenaire: { label: 'Partenaire (Marque Blanche)', icon: Users, color: 'text-purple-500' },
};

export default function ReseauLoginPage() {
  const navigate = useNavigate();
  const { type } = useParams();
  // Pré-sélection: si type dans URL, on l'utilise; sinon vide (l'utilisateur doit choisir)
  const initialType = type && MEMBER_TYPES[type] ? type : '';
  const [formData, setFormData] = useState({ email: '', password: '', member_type: initialType });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const typeConfig = MEMBER_TYPES[formData.member_type] || MEMBER_TYPES.artisan;
  const TypeIcon = typeConfig.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    if (!formData.member_type) {
      toast.error('Veuillez sélectionner votre rôle');
      return;
    }

    setLoading(true);
    try {
      // Partenaire → endpoint et stockage dédiés
      if (formData.member_type === 'partenaire') {
        const response = await axios.post(`${API}/partenaire/login`, {
          email: formData.email,
          password: formData.password,
        });
        localStorage.setItem('partner_token', response.data.access_token);
        localStorage.setItem('partner_data', JSON.stringify(response.data.partner));
        // Effacer tout token client pour éviter les conflits dans ExpertDashboard
        localStorage.removeItem('token');
        toast.success('Connexion réussie !');
        navigate('/partenaire/dashboard');
        return;
      }

      const response = await axios.post(`${API}/reseau/login`, {
        email: formData.email,
        password: formData.password,
        member_type: formData.member_type
      });
      
      // Store token and member data
      localStorage.setItem('network_token', response.data.access_token);
      localStorage.setItem('network_member', JSON.stringify(response.data.member));
      
      toast.success('Connexion réussie !');
      navigate('/reseau/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Identifiants incorrects';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex flex-col">
      {/* Header */}
      <header className="py-6 px-6 border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <h1 className="font-heading text-xl font-bold text-white">
              L'ŒIL DU MARCHAND
            </h1>
          </div>
          <span className="text-gold font-medium">ESPACE RÉSEAU</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-white/5 backdrop-blur border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <Network className="w-8 h-8 text-emerald-400" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              Connexion Réseau
            </CardTitle>
            <p className="text-white/60 text-sm mt-2">
              Accédez à votre espace membre
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-white/80">Je me connecte en tant que <span className="text-red-400">*</span></Label>
                <Select
                  value={formData.member_type}
                  onValueChange={(val) => setFormData({...formData, member_type: val})}
                >
                  <SelectTrigger
                    className="mt-1 bg-white/10 border-white/20 text-white"
                    data-testid="login-member-type"
                  >
                    <SelectValue placeholder="Sélectionnez votre rôle" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(MEMBER_TYPES).map(([key, cfg]) => {
                      const Icon = cfg.icon;
                      return (
                        <SelectItem key={key} value={key} data-testid={`login-role-${key}`}>
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${cfg.color}`} />
                            {cfg.label}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <p className="text-white/40 text-xs mt-1">
                  Si vous avez plusieurs rôles avec le même email, choisissez celui auquel vous voulez vous connecter.
                </p>
              </div>

              <div>
                <Label className="text-white/80">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="votre@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div>
                <Label className="text-white/80">Mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gold hover:text-gold/80 transition-colors z-10"
                    style={{ cursor: 'pointer' }}
                  >
                    {showPassword ? <EyeOff className="w-6 h-6" /> : <Eye className="w-6 h-6" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-6"
                data-testid="login-submit"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-white/50 text-sm">
                Pas encore membre ?{' '}
                <button
                  onClick={() => navigate('/reseau')}
                  className="text-emerald-400 hover:underline"
                >
                  Rejoindre le réseau
                </button>
              </p>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="py-4 px-6 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-white/40 text-sm">
            L'Œil du Marchand - Réseau de Professionnels
          </p>
        </div>
      </footer>
    </div>
  );
}
